import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Toast } from 'primeng/toast';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, of } from 'rxjs';

import { UsersService } from '../../core/services/users.service';
import { ContentCardComponent } from '../../shared/components/content-card/content-card.component';
import {
  PersonTableActionEvent,
  PersonTableComponent,
  PersonTablePageChange,
} from '../../shared/components/person-table/person-table.component';
import { BUSQUEDA_NOMBRE_DELAY_MS } from '../../shared/helpers/busqueda-nombre-delay';
import { mapUserToPersonTableRow } from '../../shared/helpers/map-congregacion-row';
import { PersonTableColumn, PersonTableRow } from '../../shared/interfaces/person-table.interface';
import { UserListItem } from '../../shared/interfaces/user.interface';

const PAGE_SIZE = 12;

const COLUMNAS: PersonTableColumn[] = [
  { key: 'opciones', header: 'Opciones' },
  { key: 'nombre', header: 'Nombre y Apellido' },
  { key: 'edad', header: 'Edad' },
  { key: 'lider', header: 'Líder' },
  { key: 'celular', header: 'Celular' },
  { key: 'localidad', header: 'Localidad' },
];

@Component({
  selector: 'app-papelera',
  standalone: true,
  imports: [
    FormsModule,
    ConfirmDialog,
    ContentCardComponent,
    PersonTableComponent,
    IconField,
    InputIcon,
    InputText,
    Toast,
  ],
  templateUrl: './papelera.component.html',
  styleUrl: './papelera.component.scss',
})
export class PapeleraComponent implements OnInit, OnDestroy {
  private readonly usersService = inject(UsersService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly messages = inject(MessageService);
  private readonly search$ = new Subject<string>();

  private loadSub?: Subscription;
  private searchSub?: Subscription;

  readonly columnas = COLUMNAS;
  readonly pageSize = PAGE_SIZE;
  readonly personas = signal<UserListItem[]>([]);
  readonly busqueda = signal('');
  readonly esperandoBusqueda = signal(false);
  readonly page = signal(1);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly loadError = signal(false);

  readonly rows = computed<PersonTableRow[]>(() => this.personas().map(mapUserToPersonTableRow));
  readonly first = computed(() => (this.page() - 1) * PAGE_SIZE);
  readonly vacio = computed(() => {
    if (this.loadError()) {
      return 'No se pudo cargar la papelera.';
    }
    return this.busqueda()
      ? 'No hay personas que coincidan con la búsqueda.'
      : 'La papelera está vacía.';
  });

  ngOnInit(): void {
    this.load();
    this.searchSub = this.search$
      .pipe(debounceTime(BUSQUEDA_NOMBRE_DELAY_MS), distinctUntilChanged())
      .subscribe((value) => {
        this.esperandoBusqueda.set(false);
        this.busqueda.set(value);
        this.page.set(1);
        this.load();
      });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.esperandoBusqueda.set(value.trim() !== this.busqueda().trim());
    this.search$.next(value);
  }

  onTablePageChange(event: PersonTablePageChange): void {
    if (event.page === this.page()) {
      return;
    }
    this.page.set(event.page);
    this.load();
  }

  onMenu(event: PersonTableActionEvent): void {
    if (event.action === 'restaurar') {
      this.confirmarRestaurar(event.row);
      return;
    }
    if (event.action === 'eliminarSistema') {
      this.confirmarEliminar(event.row);
    }
  }

  private confirmarRestaurar(persona: PersonTableRow): void {
    this.confirmation.confirm({
      header: 'Restaurar',
      message: 'Si confirma, este dato será enviado a la sección Congregación.',
      icon: 'pi pi-undo',
      acceptLabel: 'Restaurar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.usersService.restore(persona.id).subscribe({
          next: () => {
            this.messages.add({ severity: 'success', summary: 'Listo', detail: 'Persona restaurada.' });
            this.load();
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo restaurar.',
            });
          },
        });
      },
    });
  }

  private confirmarEliminar(persona: PersonTableRow): void {
    this.confirmation.confirm({
      header: 'Seguro?',
      message: 'Si confirma, este dato será eliminado permanentemente de la plataforma.',
      icon: 'pi pi-exclamation-triangle confirm-icon-permanente',
      acceptLabel: 'Si, eliminar!',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.usersService.delete(persona.id, true).subscribe({
          next: () => {
            this.messages.add({
              severity: 'success',
              summary: 'Listo',
              detail: 'Persona eliminada del sistema.',
            });
            this.load();
          },
          error: () => {
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo eliminar.',
            });
          },
        });
      },
    });
  }

  private load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.loadSub?.unsubscribe();

    const q = this.busqueda().trim();
    this.loadSub = this.usersService
      .listPapelera({
        page: this.page(),
        limit: PAGE_SIZE,
        q: q || undefined,
      })
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            return of({
              success: true,
              data: [],
              pagination: { page: this.page(), limit: PAGE_SIZE, total: 0 },
            });
          }
          this.loadError.set(true);
          return of({
            success: false,
            data: [],
            pagination: { page: this.page(), limit: PAGE_SIZE, total: 0 },
          });
        }),
      )
      .subscribe({
        next: (res) => {
          const total = res.pagination?.total ?? 0;
          if (total === 0 && this.page() > 1) {
            this.page.set(this.page() - 1);
            this.load();
            return;
          }
          this.personas.set(res.data ?? []);
          this.totalRecords.set(total);
          this.loading.set(false);
        },
      });
  }
}
